// skipcq: JS-W1028

const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True when `value` is a real calendar date in YYYY-MM-DD form (not e.g. 2026-02-31).
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidEventLocalDateString(value) {
  const trimmed = String(value ?? '').trim();
  if (!ISO_DATE_SHAPE.test(trimmed)) {
    return false;
  }
  const parts = trimmed.split('-').map((segment) => parseInt(segment, 10));
  const [year, month, day] = parts;
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return false;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const constructed = new Date(year, month - 1, day);
  return (
    constructed.getFullYear() === year &&
    constructed.getMonth() === month - 1 &&
    constructed.getDate() === day
  );
}

/**
 * Empty or whitespace-only is allowed; otherwise must be a parseable https URL.
 * @param {unknown} link
 * @returns {boolean}
 */
export function isValidOptionalEventHttpsLink(link) {
  const trimmed = String(link ?? '').trim();
  if (!trimmed) {
    return true;
  }
  try {
    return new URL(trimmed).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Non-empty URL safe for use as an href: only http: and https: schemes.
 * @param {unknown} url
 * @returns {boolean}
 */
export function isSafeHttpUrlForLink(url) {
  const trimmed = String(url ?? '').trim();
  if (!trimmed) {
    return false;
  }
  try {
    const protocol = new URL(trimmed).protocol;
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

const HM_TIME_SHAPE = /^([01]?\d|2[0-3]):[0-5]\d$/;

/**
 * True if empty or a valid HH:mm (24h) string.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidOptionalEventHmTime(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return true;
  }
  return HM_TIME_SHAPE.test(trimmed);
}

/**
 * Normalize optional start/end times: both empty, or both valid HH:mm with end >= start.
 * @param {unknown} startRaw
 * @param {unknown} endRaw
 * @returns {{ eventStartTime: string, eventEndTime: string }}
 */
export function normalizeOptionalEventTimeRange(startRaw, endRaw) {
  const startTrimmed = String(startRaw ?? '').trim();
  const endTrimmed = String(endRaw ?? '').trim();
  if (!startTrimmed && !endTrimmed) {
    return { eventStartTime: '', eventEndTime: '' };
  }
  if (!startTrimmed || !endTrimmed) {
    throw new Error('Set both start and end time, or leave both empty.');
  }
  if (!HM_TIME_SHAPE.test(startTrimmed) || !HM_TIME_SHAPE.test(endTrimmed)) {
    throw new Error('Times must be HH:mm (24-hour), e.g. 12:00.');
  }
  const toMinutes = (hm) => {
    const [hourPart, minutePart] = hm.split(':');
    return parseInt(hourPart, 10) * 60 + parseInt(minutePart, 10);
  };
  if (toMinutes(endTrimmed) < toMinutes(startTrimmed)) {
    throw new Error('End time must be the same as or after start time.');
  }
  return { eventStartTime: startTrimmed, eventEndTime: endTrimmed };
}
